// CONVERT BALANCE v2 — one party's box from one asset to another (UI rebuild batch 3; Astra's convert-party).
//
// THE STATE MACHINE IS screens/ConvertBalance.tsx's ConvertBalanceForm, COPIED VERBATIM: the party read and its key, the
// boxes and the one auto-pick, pickFrom, the whole-balance preset, the shared calculator, `unknown` (a failed refresh is
// unknown — nothing claimed, nothing posted), deriveConversion (imported: the four cases live there), the band check,
// the deal id minted once and held across a retry, the repeat guard, `confirm_over_balance` and its "Convert anyway"
// door, the not-netted line, the posted / already-posted lines, ServerLine. Only the drawing changed:
//
//   Astra's order — Party (read-only context), From asset | From amount, To asset | To amount, Rate — From and To side
//   by side at lg, one under the other on the phone; Notes and the As-of chips under "More"; "Before confirming" reads
//   the two boxes back before → after in the Books' words (BoxLines) with the rate both ways; the primary is "Review
//   conversion", then the confirm naming the figure. The explanation is "About this job", behind a tap.
import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { tg } from '../../../lib/telegram';
import { ASSETS } from '../../../lib/workflow';
import { amountProblem, rateProblem } from '../../../lib/amount';
import { isSentinel, nothingLanded, ApiError } from '../../../lib/errors';
import { useTradeMath } from '../../../hooks/useTradeMath';
import { bandWords } from '../../../lib/trade-math';
import { AsOfChips } from '../../AsOfChips';
import { Field } from '../../Field';
import { BalanceRows, FigureRow, RateRow } from '../record/BalanceRows';
import { ErrorLine, WarnLine } from '../../ErrorLine';
import { RepeatPanel, useRepeatGuard } from '../../RepeatPanel';
import type { Desk } from '../../DeskSwitch';
import { EmptyState, Pending, ReadFailed, sayFirst, ServerLine, StateLine, TAP } from '../../kit';
import { partyName, foldAsset, sameAsset, compareRelevance } from '../../../lib/naming';
import { ledgerText } from '../../../lib/ledger-scale';
import { displayAsset, fmtAmount, fmtDay, fmtRate, dubaiDate, dubaiWallToIso, backdateProblem } from '../../../lib/format';
import { deriveConversion, ConvertAbout } from '../../../screens/ConvertBalance';
import { Fold, FormCard, FormFrame, Review, ReviewThenConfirm } from './layout';
import { Receipt, ReceiptAmount, postedAsOf } from '../Receipt';

/** The registry's assets, FOLDED — a party's box speaks USDT, never USDT·TRC (ConvertBalance.tsx's own list). */
const BOX_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))];

export function PartyConvertForm({ slug, entity, change }: { slug: string; entity: Desk; change?: ReactNode }) {
  const nav = useNavigate();

  // Same key as the party page, so arriving from it is a cache hit.
  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug, entity),
  });

  const [fromAsset, setFromAsset] = useState<string | null>(null);
  const [toAsset, setToAsset]     = useState<string | null>(null);
  const math = useTradeMath(fromAsset ?? '', toAsset ?? '');
  const [notes, setNotes]   = useState('');
  const [when, setWhen]     = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // EVERY HOOK ABOVE THE DATA GUARDS (the party page's own lesson, 2 Sept).
  const balances = q.data?.balances ?? {};
  // A REFRESH THAT FAILED keeps the last good read beside the error: nothing is CLAIMED from it and nothing is posted
  // off it (Appendix B) — the plan, the over-balance warning, the empty claim and the post-anyway doors follow this.
  const unknown = q.isError;
  const boxes = useMemo(
    () => Object.entries(balances)
      .filter(([, v]) => v !== 0)
      .map(([asset, balance]) => ({ asset, balance }))
      .sort(compareRelevance),
    [balances],
  );
  // The most relevant box is picked ONCE, on first read — never after a posting.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || !q.data) return;
    autoPicked.current = true;
    if (boxes[0]) setFromAsset(boxes[0].asset);
  }, [q.data, boxes]);

  // The to asset cannot be the from asset: picking the to's asset as from clears the to — in the pick, not an effect.
  const pickFrom = (asset: string) => {
    setFromAsset(asset);
    setToAsset((t) => (t && sameAsset(t, asset) ? null : t));
  };

  const balance = fromAsset ? (balances[fromAsset] ?? 0) : 0;
  const pairKnown = !!fromAsset && !!toAsset;
  // THE BALANCE IS THE ANCHOR — the whole balance preset on a from pick, and once more as the pair forms
  // (ConvertBalance.tsx explains the key; `toAsset` is deliberately not in it).
  useEffect(() => {
    if (!fromAsset) return;
    math.preset({ asset: fromAsset }, balance !== 0 ? ledgerText(Math.abs(balance), fromAsset) : '');
  }, [fromAsset, balance, pairKnown]);   // eslint-disable-line react-hooks/exhaustive-deps

  const fromText = fromAsset ? math.amountOf(fromAsset) : '';
  const toText   = toAsset   ? math.amountOf(toAsset)   : '';
  const fromN = Number(fromText) || 0;
  const toN   = Number(toText)   || 0;
  const rateN = Number(math.rate) || 0;
  const fromWords = amountProblem(fromText);
  const toWords   = amountProblem(toText);
  const rateProblemWords = rateProblem(math.rate);
  const over = !unknown && fromAsset && fromN > Math.abs(balance) ? fromN - Math.abs(balance) : 0;
  const fromAuto = !!fromAsset && math.isDerived(fromAsset);
  const toAuto   = !!toAsset   && math.isDerived(toAsset);

  const plan = useMemo(() => {
    if (unknown || !fromAsset || !toAsset || balance === 0 || fromN <= 0 || toN <= 0 || rateN <= 0 || fromWords || toWords || rateProblemWords) return null;
    return deriveConversion({ balance, fromAsset, toAsset, fromAmount: fromN, toAmount: toN, rate: math.rate });
  }, [unknown, fromAsset, toAsset, balance, fromN, toN, rateN, fromWords, toWords, rateProblemWords, math.rate]);
  // Why Review is off, in a few words. A typed figure that is wrong and the date say their own words on the form.
  // Every reason the confirm can be off has words — a read that failed included (Astra, 1 Nov 2026: no dead button).
  const missing = unknown ? 'Balances not read — try again.'
    : !fromAsset ? 'Pick the balance to convert.'
    : !toAsset ? 'Pick what it converts to.'
    : balance === 0 ? 'Nothing held in this asset.'
    : !(fromN > 0) ? 'Enter the amount.'
    : !(toN > 0) && !(rateN > 0) ? 'Enter the rate or what it converts to.'
    : fromWords || toWords || rateProblemWords ? 'Check the figures.'
    : whenWords ? 'Check the date.'
    : null;

  // OFF-BAND — the shared table; warn, never block.
  const band = useMemo(() => {
    if (!pairKnown || rateProblemWords) return null;
    const w = bandWords(math.base, math.quote, rateN);
    return w;
  }, [pairKnown, rateN, rateProblemWords, math.base, math.quote]);
  const bandSaid = band ? sayFirst(band) : null;
  const bandDerived = math.derived === 'rate';

  // ── submit ─────────────────────────────────────────────────────────────── (ConvertBalanceForm's, verbatim)
  const dealIdRef = useRef<string | null>(null);
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const [posted, setPosted] = useState<{
    at: number; dealId: string; deduped: boolean; asOf: string | null;
    converted: boolean;
    name: string; from: string; to: string; fromAmount: number; toAmount: number; fromAfter: number; toAfter: number;
  } | null>(null);

  const post = async (confirmRepeat: boolean, confirmOver = false) => {
    if (!plan || !fromAsset || !toAsset) throw new Error('nothing to convert');
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    const data = await api.bookTrade({
      entity,
      direction:  plan.direction,
      party_slug: slug,
      unsettled:         true,
      settle_on_account: true,
      amount_take: plan.take,
      amount_give: plan.give,
      rate:        plan.rate,
      deal_id:     dealId,
      notes:       notes.trim() || undefined,
      effective_at:   whenIso || undefined,
      confirm_repeat: confirmRepeat || undefined,
      confirm_over_balance: confirmOver || undefined,
    });
    const fromAfter = balance - plan.sign * fromN;
    const toAfter   = (balances[toAsset] ?? 0) + plan.sign * plan.converted;
    return {
      data, dealId, asOf: whenIso || null, name: q.data ? partyName(q.data.party) : slug,
      from: fromAsset, to: toAsset, fromAmount: fromN, toAmount: plan.converted, fromAfter, toAfter,
    };
  };
  const submit = useMutation({
    mutationFn: (opts?: { confirmRepeat?: boolean; confirmOver?: boolean }) => guard(() => post(opts?.confirmRepeat === true, opts?.confirmOver === true)),
    onSuccess: ({ data, dealId, ...facts }) => {
      clearTwin();
      dealIdRef.current = null;
      setPosted({ at: Date.now(), dealId: data.deal_id ?? dealId, deduped: !!data.deduped, converted: data.converted === true, ...facts });
      setFromAsset(null); setToAsset(null); math.reset(); setWhen(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; clearTwin(); }
  };

  // REVIEW, THEN CONFIRM (Astra's primary). Any change to what would post sends the form back to "Review conversion":
  // the confirm only ever stands under the figures it names. A refusal leaves them as they were, so the confirm stays
  // for a deliberate second press — never pressed for him.
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); }, [fromAsset, toAsset, fromText, toText, math.rate, when]);

  if (q.isError && !q.data) return <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} tone="bad" />;
  if (!q.data)   return <Pending />;

  const name = partyName(q.data.party);
  const partyHref = `/parties/${encodeURIComponent(q.data.party.slug)}${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  // A RAIL IS NOT A PARTY HERE — a pasted /parties/<rail-slug>/convert would post against an account no party holds.
  if (q.data.party.party_type === 'own_account') {
    return (
      <StateLine tone="warn" action={<Link to={partyHref} className={TAP}>Open account →</Link>}>
        {name} is a desk account — no box to convert.
      </StateLine>
    );
  }

  const asOfWords = whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : '';
  const partyLine = (
    <Field label="Party">
      <div className="flex min-h-[44px] items-center justify-between gap-3 lg:min-h-[40px]">
        <Link to={partyHref} className="tap-44 min-w-0 break-words text-body font-medium text-tg-text hover:underline">{name}</Link>
        {change}
      </div>
    </Field>
  );

  // Nothing to convert: a claim from a read that SUCCEEDED; after a failed refresh the warning is all that is said.
  if (boxes.length === 0 && !posted) {
    return (
      <FormCard>
        {partyLine}
        {unknown
          ? <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} />
          : <EmptyState action={<Link to={partyHref} className={TAP}>Open {name} →</Link>}>Nothing to convert.</EmptyState>}
      </FormCard>
    );
  }

  /* THEIR BALANCES, BESIDE THE FORM (his "I need their balance on side table for effective conversion", 1 Nov 2026): every box
     the party holds, in the box's words; the two this conversion touches say where they go ("owes you 4.28 → 0"). A row is
     a tap that makes it the From asset. */
  const sideBalances = (
    <div>
      {/* The party is named once, in the form's Party row (1 Nov 2026 audit); after a posting, until the re-read lands, the
          figures are said to be the ones from before it — never the old balances passed off as current. */}
      <h2 className="mb-2 text-title font-semibold">{/* Beside the form (xl) the heading names the party; below xl the Party row right under it already does. */}<span className="max-xl:hidden">{name}&rsquo;s balances</span><span className="xl:hidden">Balances</span>{posted && q.dataUpdatedAt < submit.submittedAt ? ' before this conversion' : ''}</h2>
      {posted && q.isFetching && <p className="mb-2 text-meta text-tg-hint"><Pending inline what="Reading the balances again…" /></p>}
      {boxes.length === 0 ? <p className="text-meta text-tg-hint">square</p> : (
        <ul className="space-y-1.5">
          {boxes.map(({ asset, balance: b }) => {
            const isFrom = !!fromAsset && sameAsset(asset, fromAsset), isTo = !!toAsset && sameAsset(asset, toAsset);
            const after = plan && isFrom ? b - plan.sign * fromN : plan && isTo ? b + plan.sign * plan.converted : undefined;
            return (
              <li key={asset}>
                <button type="button" className={`w-full text-left ${isFrom ? 'font-medium' : ''}`} disabled={submit.isPending} onClick={() => pickFrom(asset)}
                  title={`Convert from ${displayAsset(asset)}`}>
                  <span className="block text-meta text-tg-hint">{displayAsset(asset)}</span>
                  <BalanceRows className="!mt-0.5" lines={[{ asset, now: b, after }]} />
                </button>
              </li>
            );
          })}
          {plan && toAsset && !boxes.some((x) => sameAsset(x.asset, toAsset)) && (
            <li><span className="block text-meta text-tg-hint">{displayAsset(toAsset)}</span><BalanceRows className="!mt-0.5" lines={[{ asset: toAsset, now: 0, after: plan.sign * plan.converted }]} /></li>
          )}
        </ul>
      )}
    </div>
  );

  // ONE FACT PER ROW (his "check everything where it applies", 1 Nov 2026): the two figures, then the rate and its inverse
  // each on its own labelled line — never two equations in one cell.
  const readBack = plan && fromAsset && toAsset ? (
    <div className="space-y-1.5">
      {/* Where the two balances go is in the balances block (sideBalances): beside the form from xl, above it below. */}
      <div className="flex items-baseline justify-between gap-3 text-body"><span className="text-tg-hint">Party</span><span className="min-w-0 break-words text-right font-medium">{name}</span></div>
      <FigureRow label="Converts" value={fromN} asset={fromAsset} />
      <FigureRow label="Into" value={plan.converted} asset={toAsset} strong />
      <RateRow label="Rate" base={math.base} quote={math.quote} rate={rateN} />
      <RateRow label="Inverse" base={math.quote} quote={math.base} rate={1 / rateN} quiet approx />
      {asOfWords && <div className="flex justify-between gap-3 text-body"><span className="text-tg-hint">As of</span><span>{fmtDay(whenIso!)}</span></div>}
      {/* What the posting carries besides the figures (Astra, 1 Nov 2026): it settles on their account, and the note. */}
      <div className="flex justify-between gap-3 text-body"><span className="text-tg-hint">Settles</span><span>On their account</span></div>
      {notes.trim() !== '' && <div className="flex justify-between gap-3 text-body"><span className="text-tg-hint">Notes</span><span className="min-w-0 break-words text-right">{notes.trim()}</span></div>}
    </div>
  ) : null;

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what was CONVERTED, from what
     posted; the party's balances stay beside it, re-read. A booking the books did not confirm as netted says so first. */
  if (submit.isSuccess && posted) {
    return (
      <div className="xl:grid xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:items-start xl:gap-8">
        <FormCard>
          <Receipt
            title={posted.converted ? 'Converted' : 'Booked, not confirmed as netted'}
            tone={posted.converted ? 'good' : 'warn'}
            deduped={posted.deduped}
            already="Already booked"
            movements={partyHref}
            movementsLabel="Open party"
            reference={posted.dealId}
            facts={[
              { label: 'Converted', value: <ReceiptAmount value={posted.fromAmount} asset={posted.from} /> },
              { label: 'Into', value: <ReceiptAmount value={posted.toAmount} asset={posted.to} /> },
              { label: 'Party', value: posted.name },
              { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
            ]}
            extra={!posted.converted && (
              <StateLine tone="warn" action={<Link to="/settle" className={TAP}>Settle later →</Link>}>Check it before converting again.</StateLine>
            )}
            another={{ label: 'Convert another', onClick: () => submit.reset() }}
            links={<Link to={partyHref} className={TAP}>Open party →</Link>}
          />
        </FormCard>
        <aside className="hidden min-w-0 xl:block xl:pt-2">{sideBalances}</aside>
      </div>
    );
  }

  return (
    <FormFrame
      side={sideBalances}
      reviewReady={!!readBack}
      reviewing={reviewing}
      warnings={(bandSaid || (!fromWords && over > 0 && fromAsset)) ? (
        <div className="space-y-2">
          {!fromWords && over > 0 && fromAsset && <WarnLine>Over by {fmtAmount(over, fromAsset)} — the box flips.</WarnLine>}
          {bandSaid && (
            <StateLine tone="warn" more={bandSaid.rest || undefined}>
              {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
            </StateLine>
          )}
        </div>
      ) : null}
      review={<Review desk={entity} readAt={q.dataUpdatedAt} help={<ConvertAbout />}>{reviewing ? readBack : null}</Review>}
      footer={(
        <ReviewThenConfirm
          reviewing={reviewing}
          setReviewing={setReviewing}
          disabled={submit.isPending || !plan || !!whenWords}
          why={missing}
          pending={submit.isPending}
          confirmLabel={`Convert ${plan && fromAsset ? `${fmtAmount(fromN, fromAsset)} ${displayAsset(fromAsset)}` : ''}${plan && toAsset ? ` to ${displayAsset(toAsset)}` : ''}${asOfWords}`}
          onConfirm={() => submit.mutate({})}
        />
      )}
      outcome={(
        <>
          {q.isError && <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} />}
          {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
          {/* The Worker refuses a conversion larger than the balance it converts; the operator can say it is intended. */}
          {submit.isError && submit.error instanceof ApiError && submit.error.code === 'over_balance' && (
            <button className="btn-secondary text-body w-full lg:w-auto" disabled={unknown} onClick={() => submit.mutate({ confirmOver: true })}>
              Convert anyway — flip the balance the other way
            </button>
          )}
          {repeat && (
            <RepeatPanel
              twin={repeat}
              pending={submit.isPending || !!whenWords || unknown}
              onAlreadyBooked={() => { clearTwin(); nav(-1); }}
              onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
            />
          )}
        </>
      )}
    >
      {partyLine}

      {/* FROM and TO: side by side from lg, one under the other on the phone — each its asset beside its amount. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid grid-cols-2 items-start gap-3 lg:grid-cols-1">
          <Field label="From asset">
            <select name="from_asset" className="select w-full" value={fromAsset ?? ''} onChange={(e) => pickFrom(e.target.value)} disabled={boxes.length === 0}>
              {!fromAsset && <option value="">Pick</option>}
              {boxes.map((b) => <option key={b.asset} value={b.asset}>{displayAsset(b.asset)}</option>)}
            </select>
          </Field>
          <Field label="From amount" hint={fromAuto ? 'auto' : undefined}>
            <input
              name="from_amount"
              className={`input${fromAuto ? ' text-tg-hint' : ''}`}
              value={fromText}
              onChange={(e) => { if (fromAsset) math.setAmount(fromAsset, e.target.value); }}
              inputMode="decimal"
              disabled={!fromAsset}
            />
            {/* The box as it stands, in the Books' words — what the amount is taken from. */}
            <ErrorLine>{fromWords}</ErrorLine>
            {!fromWords && over > 0 && fromAsset && (
              <WarnLine>Over by {fmtAmount(over, fromAsset)} — the box flips.</WarnLine>
            )}
          </Field>
        </div>
        <div className="grid grid-cols-2 items-start gap-3 lg:grid-cols-1">
          <Field label="To asset">
            <select name="to_asset" className="select w-full" value={toAsset ?? ''} onChange={(e) => setToAsset(e.target.value || null)}>
              <option value="">Pick</option>
              {BOX_ASSETS.filter((a) => !fromAsset || !sameAsset(a, fromAsset)).map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
            </select>
          </Field>
          <Field label="To amount" hint={toAuto ? 'auto' : undefined}>
            <input
              name="to_amount"
              className={`input${toAuto ? ' text-tg-hint' : ''}`}
              value={toText}
              onChange={(e) => { if (toAsset) math.setAmount(toAsset, e.target.value); }}
              inputMode="decimal"
              disabled={!pairKnown}
            />
            <ErrorLine>{toWords}</ErrorLine>
          </Field>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Field label="Rate" hint={pairKnown ? `${math.rateWords}${math.rateIsDerived ? ' · auto' : ''}` : undefined}>
          <input
            name="rate"
            className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
            value={math.rate}
            onChange={(e) => math.setRate(e.target.value)}
            inputMode="decimal"
            disabled={!pairKnown}
          />
          <ErrorLine>{rateProblemWords}</ErrorLine>
        </Field>
      </div>
      {bandSaid && (
        <StateLine tone="warn" more={bandSaid.rest || undefined}>
          {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
        </StateLine>
      )}

      <Fold label="More">
        <AsOfChips value={when} onChange={onWhenChange} disabled={submit.isPending} />
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </Fold>
    </FormFrame>
  );
}
